'use client'

import { useEffect, useMemo, useRef } from 'react'

import { cn } from '@/lib/utils'

/**
 * Procedural "ribbon" field (ported from the leaf sample). A single virtual
 * 1720×1080 field of `count` flowing strands is rendered with a vertical
 * brand gradient (mint→sky), optional edge fade, a reveal-on-load dash
 * animation, and a sparse particle system. The same field can be drawn into
 * differently sized/positioned canvases (with cover-scaling).
 */

export type RibbonSettings = {
  width: number
  height: number
  count: number
  resolution: number
  spacing: number
  amplitude: number
  frequency: number
  phase: number
  twist: number
  bulge: number
  morph: number
  speed: number
  anim: 'spin' | 'flow' | 'morph' | 'breathe' | 'none'
  strokeWidth: number
  opacity: number
  gradientStart: string
  gradientEnd: string
  background: string
  particlesOn: boolean
  particleColors: string[]
  particleCount: number
  particleSize: number
  particleLength: number
  particleFade: boolean
  particleSpeed: number
  particleCoverage: number
  particleRandom: boolean
  particleSparse: number
  particleVariance: number
  particleSeed: number
  rotate: number
  edgeFade: number
  edgeFadeStart?: number
  edgeFadeEnd?: number
}

// Colours are the existing Euclideum palette: brand mint #37ecb0 and
// brand sky #0ea5e9 (same accents used across the hero/solutions UI).
export const DEFAULT_SETTINGS: RibbonSettings = {
  width: 1720,
  height: 1080,
  count: 24,
  resolution: 81,
  spacing: 4.9,
  amplitude: 163,
  frequency: 0.75,
  phase: 0.14,
  twist: 0.41,
  bulge: 0.66,
  morph: 0.5,
  speed: 1,
  anim: 'spin',
  strokeWidth: 1,
  opacity: 1,
  gradientStart: '#37ecb0',
  gradientEnd: '#0ea5e9',
  background: 'transparent',
  particlesOn: true,
  particleColors: ['#37ecb0', '#0ea5e9', '#ffffff'],
  particleCount: 1,
  particleSize: 3,
  particleLength: 88,
  particleFade: true,
  particleSpeed: 1.15,
  particleCoverage: 1,
  particleRandom: true,
  particleSparse: 0.32,
  particleVariance: 0,
  particleSeed: 2025,
  rotate: 0,
  edgeFade: 0,
}

type Pt = { x: number; y: number }
type Grad = { solid: string; clear: string; full: string }

/**
 * Per-canvas scratch state. Kept per-instance (not module-level) so that
 * several ribbon canvases animating at the same time never stomp each
 * other's point/gradient buffers.
 */
type RibbonState = {
  pointCache: Pt[][]
  distBuf: number[]
  gradKey: string
  gradCache: Grad[]
  pairs: Map<string, [string, string]>
  // Per-strand edge-fade gradients, reused across frames while the strand
  // endpoints stay near where the gradient was built (they wobble slowly).
  edgeGrads: (CanvasGradient | null)[]
  edgeGradPos: [number, number, number, number][]
}

function createRibbonState(): RibbonState {
  return {
    pointCache: [],
    distBuf: [],
    gradKey: '',
    gradCache: [],
    pairs: new Map(),
    edgeGrads: [],
    edgeGradPos: [],
  }
}

function hexToRgb(hex: string): [number, number, number] {
  let t = hex.replace('#', '')
  if (t.length === 3)
    t = t
      .split('')
      .map((c) => c + c)
      .join('')
  return [
    parseInt(t.slice(0, 2), 16),
    parseInt(t.slice(2, 4), 16),
    parseInt(t.slice(4, 6), 16),
  ]
}

function rgba(hex: string, a: number) {
  const [r, g, b] = hexToRgb(hex)
  return `rgba(${r},${g},${b},${a})`
}

function lerpColor(a: string, b: string, n: number): [number, number, number] {
  const x = hexToRgb(a)
  const y = hexToRgb(b)
  return [
    Math.round(x[0] + (y[0] - x[0]) * n),
    Math.round(x[1] + (y[1] - x[1]) * n),
    Math.round(x[2] + (y[2] - x[2]) * n),
  ]
}

// deterministic hash → [0,1)
function hash(e: number, t: number, n: number) {
  let r = (0x165667b1 * e + 0x27d4eb2f * t + 0x3a8f05c5 * n) | 0
  r = (r ^ (r >>> 13)) * 0x4bf19f61
  r ^= r >>> 16
  return ((r >>> 0) % 100000) / 100000
}

function strandShape(
  lineIdx: number,
  f: number,
  time: number,
  s: RibbonSettings
) {
  const count = s.count
  const phase = s.phase * (lineIdx - (count - 1) / 2)
  const w = s.frequency * Math.PI * 2
  const bulge = 1 + s.bulge * Math.sin(f * Math.PI)
  let flow = 0
  let morphT = 0
  let breathe = 1
  let spin = 0
  if (s.anim === 'flow') flow = 0.8 * time
  else if (s.anim === 'morph') morphT = 0.6 * time
  else if (s.anim === 'breathe') breathe = 1 + 0.35 * Math.sin(1.2 * time)
  else if (s.anim === 'spin') spin = 0.7 * time
  const m = Math.sin(w * f + phase + flow)
  const h = Math.sin(0.5 * w * f - 2 * phase + 0.7 * flow + spin)
  let p = 0
  if (s.morph > 0)
    p =
      Math.sin(3.7 * f + morphT + 0.2 * lineIdx) *
      Math.cos(2.1 * f - 0.5 * morphT + 0.13 * lineIdx)
  const yOffset = (m + s.twist * h + s.morph * p) * 0.5 * bulge * breathe
  const lineOffset =
    ((count <= 1 ? 0.5 : lineIdx / (count - 1)) - 0.5) * count * s.spacing
  return { yOffset, lineOffset }
}

function generateLinePoints(
  state: RibbonState,
  lineIdx: number,
  time: number,
  s: RibbonSettings
) {
  const segments = Math.max(2, s.resolution) - 1
  let pts = state.pointCache[lineIdx]
  if (!pts || pts.length !== segments + 1) {
    pts = Array.from({ length: segments + 1 }, () => ({ x: 0, y: 0 }))
    state.pointCache[lineIdx] = pts
  }
  const halfH = s.height / 2
  const rot = (s.rotate * Math.PI) / 180
  const cos = Math.cos(rot)
  const sin = Math.sin(rot)
  const halfW = s.width / 2
  for (let n = 0; n <= segments; n++) {
    const f = n / segments
    const d = strandShape(lineIdx, f, time, s)
    const mx = f * s.width
    const my = halfH + d.lineOffset + d.yOffset * s.amplitude
    const pt = pts[n]!
    if (rot === 0) {
      pt.x = mx
      pt.y = my
    } else {
      const ex = mx - halfW
      const ey = my - halfH
      pt.x = halfW + ex * cos - ey * sin
      pt.y = halfH + ex * sin + ey * cos
    }
  }
  return pts
}

function getGradients(state: RibbonState, s: RibbonSettings) {
  const key = `${s.gradientStart}|${s.gradientEnd}|${s.count}|${s.opacity}`
  if (key !== state.gradKey) {
    state.gradKey = key
    state.gradCache = []
    state.edgeGrads = []
    state.edgeGradPos = []
    for (let t = 0; t < s.count; t++) {
      const n = s.count <= 1 ? 0 : t / (s.count - 1)
      const [r, g, b] = lerpColor(s.gradientStart, s.gradientEnd, n)
      state.gradCache.push({
        solid: `rgb(${r},${g},${b})`,
        clear: `rgba(${r},${g},${b},0)`,
        full: `rgba(${r},${g},${b},${s.opacity})`,
      })
    }
  }
  return state.gradCache
}

function particlePos(
  lineIdx: number,
  particleIdx: number,
  time: number,
  s: RibbonSettings
) {
  const span = Math.max(0.8, 3.4 / Math.max(0.15, Math.abs(s.particleSpeed)))
  const period = span + 2.2
  const off = 0.18 * lineIdx
  const rnd = 1.8 * hash(lineIdx, 17 * particleIdx + 3, s.particleSeed)
  let o = (time + off - 0.9 * particleIdx - rnd) % period
  if (o < 0) o += period
  if (o > span) return null
  const u = o / span
  return {
    u: u * s.particleCoverage,
    fade: u < 0.18 ? u / 0.18 : u > 0.68 ? (1 - u) / 0.32 : 1,
  }
}

function pointAtArc(
  state: RibbonState,
  pts: Pt[],
  tNorm: number
): [number, number, number, number] {
  if (pts.length === 0) return [0, 0, 0, 0]
  const first = pts[0]
  if (pts.length === 1 && first) return [first.x, first.y, 0, 0]
  let total = 0
  const last = pts.length - 1
  for (let i = 1; i < pts.length; i++) {
    const A = pts[i - 1]
    const B = pts[i]
    if (!A || !B) {
      state.distBuf[i - 1] = 0
      continue
    }
    const dd = Math.hypot(B.x - A.x, B.y - A.y)
    state.distBuf[i - 1] = dd
    total += dd
  }
  if (total === 0 && first) return [first.x, first.y, 0, 0]
  const target = tNorm * total
  let acc = 0
  for (let i = 0; i < last; i++) {
    const seg = state.distBuf[i] ?? 0
    if (acc + seg >= target) {
      const A = pts[i]
      const B = pts[i + 1]
      if (!A || !B) break
      const f = seg > 0 ? (target - acc) / seg : 0
      return [
        A.x + (B.x - A.x) * f,
        A.y + (B.y - A.y) * f,
        B.x - A.x,
        B.y - A.y,
      ]
    }
    acc += seg
  }
  const e = pts[pts.length - 1]
  return e ? [e.x, e.y, 0, 0] : [0, 0, 0, 0]
}

function particleColor(
  lineIdx: number,
  particleIdx: number,
  s: RibbonSettings
) {
  const arr = s.particleColors.length ? s.particleColors : ['#ffffff']
  const i =
    Math.floor(
      hash(lineIdx, 19 * particleIdx + 11, s.particleSeed) * arr.length
    ) % arr.length
  return arr[i] ?? '#ffffff'
}

export function drawRibbonFrame(
  state: RibbonState,
  ctx: CanvasRenderingContext2D,
  settings: Partial<RibbonSettings>,
  time: number,
  reveal = 1
) {
  const s = { ...DEFAULT_SETTINGS, ...settings }
  const { width: v, height: b } = s
  const revealing = reveal < 1
  ctx.clearRect(0, 0, v, b)
  if (s.background && s.background !== 'transparent') {
    ctx.fillStyle = s.background
    ctx.fillRect(0, 0, v, b)
  }
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  const grads = getGradients(state, s)
  ctx.lineWidth = s.strokeWidth
  const clamp = (e: number) => Math.min(0.5, Math.max(0, e))
  const efStart = clamp(s.edgeFadeStart ?? s.edgeFade)
  const efEnd = clamp(s.edgeFadeEnd ?? s.edgeFade)

  for (let t = 0; t < s.count; t++) {
    const pts = generateLinePoints(state, t, time, s)
    if (pts.length < 2) continue
    const first = pts[0]
    const last = pts[pts.length - 1]
    if (!first) continue
    const grad = grads[t]
    if (!grad) continue

    if (revealing) {
      const r = Math.min(reveal / (0.6 + 0.4 * hash(t, 53, s.particleSeed)), 1)
      const scale = r * r * (3 - 2 * r)
      if (scale <= 0) {
        ctx.setLineDash([])
        continue
      }
      if (scale < 1) {
        let total = 0
        for (let e = 1; e < pts.length; e++) {
          const A = pts[e - 1]
          const B = pts[e]
          if (A && B) total += Math.hypot(B.x - A.x, B.y - A.y)
        }
        ctx.setLineDash([total * scale, total + 1])
      } else ctx.setLineDash([])
    }

    if ((efStart > 0 || efEnd > 0) && last) {
      // Rebuild the fade gradient only when the strand's endpoints have
      // drifted meaningfully — creating 24 gradients per frame is needless
      // allocation churn, and the fade is far too soft to show a 12px lag.
      const pos = state.edgeGradPos[t]
      let g = state.edgeGrads[t]
      const moved =
        !pos ||
        !g ||
        (first.x - pos[0]) ** 2 + (first.y - pos[1]) ** 2 > 144 ||
        (last.x - pos[2]) ** 2 + (last.y - pos[3]) ** 2 > 144
      if (moved) {
        g = ctx.createLinearGradient(first.x, first.y, last.x, last.y)
        g.addColorStop(0, efStart > 0 ? grad.clear : grad.full)
        if (efStart > 0) g.addColorStop(efStart, grad.full)
        if (efEnd > 0) g.addColorStop(1 - efEnd, grad.full)
        g.addColorStop(1, efEnd > 0 ? grad.clear : grad.full)
        state.edgeGrads[t] = g
        state.edgeGradPos[t] = [first.x, first.y, last.x, last.y]
      }
      ctx.strokeStyle = g!
      ctx.globalAlpha = 1
    } else {
      ctx.strokeStyle = grad.solid
      ctx.globalAlpha = s.opacity
    }

    ctx.beginPath()
    ctx.moveTo(first.x, first.y)
    for (let e = 1; e < pts.length - 1; e++) {
      const A = pts[e]
      const B = pts[e + 1]
      if (A && B)
        ctx.quadraticCurveTo(A.x, A.y, (A.x + B.x) / 2, (A.y + B.y) / 2)
    }
    if (last) ctx.lineTo(last.x, last.y)
    ctx.stroke()
  }
  if (revealing) ctx.setLineDash([])

  if (s.particlesOn && s.particleCount > 0 && reveal >= 1) {
    ctx.globalAlpha = 1
    const pc = Math.floor(s.particleCount)
    for (let li = 0; li < s.count; li++) {
      if (s.particleRandom && hash(li, 7, s.particleSeed) > s.particleSparse)
        continue
      const pts = state.pointCache[li]
      if (!pts) continue
      for (let pi = 0; pi < pc; pi++) {
        const pos = particlePos(li, pi, time, s)
        if (pos == null) continue
        const [px, py, dx, dy] = pointAtArc(state, pts, pos.u)
        let variance = 1
        if (s.particleVariance > 0) {
          const e = hash(li, 31 * pi + 5, s.particleSeed)
          variance = Math.max(
            0.1,
            1 - s.particleVariance + 2 * e * s.particleVariance
          )
        }
        const color = particleColor(li, pi, s)
        const mag = Math.hypot(dx, dy) || 1
        const bx = dx / mag
        const by = dy / mag
        const half = s.particleLength / 2
        const w1 = px - bx * half
        const m1 = py - by * half
        const k1 = px + bx * half
        const j1 = py + by * half
        ctx.lineWidth = s.particleSize * variance
        if (s.particleFade) {
          let pair = state.pairs.get(color)
          if (!pair) {
            pair = [rgba(color, 0), rgba(color, 1)]
            state.pairs.set(color, pair)
          }
          const g = ctx.createLinearGradient(w1, m1, k1, j1)
          g.addColorStop(0, pair[0])
          g.addColorStop(1, pair[1])
          ctx.strokeStyle = g
        } else ctx.strokeStyle = color
        ctx.globalAlpha = pos.fade
        ctx.beginPath()
        ctx.moveTo(w1, m1)
        ctx.lineTo(k1, j1)
        ctx.stroke()
      }
    }
    ctx.globalAlpha = 1
  }
}

/* ─── WebGL renderer ─────────────────────────────────────────────────────
   The 2D-canvas path above rasterizes 24 gradient strokes over the whole
   canvas on the CPU each frame — at hero size that's the whole frame
   budget. The GL path uploads ~2k vertices per frame and lets the GPU
   rasterize, so full resolution at native frame rate is nearly free.
   The 2D path is kept as an automatic fallback. */

const VERT_SRC = `
attribute vec2 a_pos;
attribute vec4 a_col;
uniform vec2 u_res;
uniform vec2 u_scale;
uniform vec2 u_offset;
varying vec4 v_col;
void main() {
  vec2 px = a_pos * u_scale + u_offset;
  vec2 clip = (px / u_res) * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  v_col = a_col;
}`

const FRAG_SRC = `
precision mediump float;
varying vec4 v_col;
void main() {
  gl_FragColor = vec4(v_col.rgb * v_col.a, v_col.a);
}`

type GlRenderer = {
  gl: WebGLRenderingContext
  buf: WebGLBuffer
  uRes: WebGLUniformLocation
  uScale: WebGLUniformLocation
  uOffset: WebGLUniformLocation
  data: Float32Array
  maxLineWidth: number
}

const glColorCache = new Map<string, [number, number, number]>()
function glColor(hex: string): [number, number, number] {
  let c = glColorCache.get(hex)
  if (!c) {
    const [r, g, b] = hexToRgb(hex)
    c = [r / 255, g / 255, b / 255]
    glColorCache.set(hex, c)
  }
  return c
}

function compile(gl: WebGLRenderingContext, type: number, src: string) {
  const sh = gl.createShader(type)
  if (!sh) return null
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) return null
  return sh
}

function initGl(canvas: HTMLCanvasElement): GlRenderer | null {
  let gl: WebGLRenderingContext | null = null
  try {
    gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      premultipliedAlpha: true,
      depth: false,
      stencil: false,
    }) as WebGLRenderingContext | null
  } catch {
    return null
  }
  if (!gl) return null

  const vs = compile(gl, gl.VERTEX_SHADER, VERT_SRC)
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG_SRC)
  const prog = gl.createProgram()
  if (!vs || !fs || !prog) return null
  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.bindAttribLocation(prog, 0, 'a_pos')
  gl.bindAttribLocation(prog, 1, 'a_col')
  gl.linkProgram(prog)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null
  gl.useProgram(prog)

  const buf = gl.createBuffer()
  const uRes = gl.getUniformLocation(prog, 'u_res')
  const uScale = gl.getUniformLocation(prog, 'u_scale')
  const uOffset = gl.getUniformLocation(prog, 'u_offset')
  if (!buf || !uRes || !uScale || !uOffset) return null

  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  gl.enableVertexAttribArray(0)
  gl.enableVertexAttribArray(1)
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 24, 0)
  gl.vertexAttribPointer(1, 4, gl.FLOAT, false, 24, 8)
  gl.enable(gl.BLEND)
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)

  const range = gl.getParameter(gl.ALIASED_LINE_WIDTH_RANGE) as Float32Array

  return {
    gl,
    buf,
    uRes,
    uScale,
    uOffset,
    data: new Float32Array(0),
    maxLineWidth: range?.[1] ?? 1,
  }
}

function drawRibbonFrameGl(
  glr: GlRenderer,
  state: RibbonState,
  s: RibbonSettings,
  time: number,
  reveal: number
) {
  const { gl } = glr
  if (gl.isContextLost()) return
  const cw = gl.drawingBufferWidth
  const ch = gl.drawingBufferHeight
  gl.viewport(0, 0, cw, ch)

  if (s.background && s.background !== 'transparent') {
    const [r, g, b] = glColor(s.background)
    gl.clearColor(r, g, b, 1)
  } else {
    gl.clearColor(0, 0, 0, 0)
  }
  gl.clear(gl.COLOR_BUFFER_BIT)

  // cover-scale the virtual field into the canvas, centered (same as 2D)
  const scale = Math.max(cw / s.width, ch / s.height)
  const ox = (cw - s.width * scale) / 2
  const oy = (ch - s.height * scale) / 2
  gl.uniform2f(glr.uRes, cw, ch)
  gl.uniform2f(glr.uScale, scale, scale)
  gl.uniform2f(glr.uOffset, ox, oy)
  gl.lineWidth(Math.min(Math.max(1, s.strokeWidth * scale), glr.maxLineWidth))

  const clampEf = (e: number) => Math.min(0.5, Math.max(0, e))
  const efStart = clampEf(s.edgeFadeStart ?? s.edgeFade)
  const efEnd = clampEf(s.edgeFadeEnd ?? s.edgeFade)
  const verts = Math.max(2, s.resolution)
  const pc = s.particlesOn ? Math.floor(s.particleCount) : 0

  const needed = (s.count * verts + s.count * Math.max(1, pc) * 6 + 64) * 6
  if (glr.data.length < needed) glr.data = new Float32Array(needed)
  const data = glr.data

  // ── Strands: one LINE_STRIP per strand, per-vertex edge-fade alpha ──
  const strandRanges: [number, number][] = []
  let o = 0
  const revealing = reveal < 1
  for (let t = 0; t < s.count; t++) {
    const pts = generateLinePoints(state, t, time, s)
    if (pts.length < 2) continue

    let drawCount = pts.length
    if (revealing) {
      const r = Math.min(reveal / (0.6 + 0.4 * hash(t, 53, s.particleSeed)), 1)
      const sc = r * r * (3 - 2 * r)
      if (sc <= 0) continue
      drawCount = Math.max(2, Math.round(sc * pts.length))
    }

    const n = s.count <= 1 ? 0 : t / (s.count - 1)
    const [cr, cg, cb] = lerpColor(s.gradientStart, s.gradientEnd, n)
    const r01 = cr / 255
    const g01 = cg / 255
    const b01 = cb / 255

    const start = o / 6
    const denom = pts.length - 1
    for (let i = 0; i < drawCount; i++) {
      const p = pts[i]!
      const f = i / denom
      let a = s.opacity
      if (efStart > 0 && f < efStart) a *= f / efStart
      if (efEnd > 0 && f > 1 - efEnd) a *= (1 - f) / efEnd
      data[o++] = p.x
      data[o++] = p.y
      data[o++] = r01
      data[o++] = g01
      data[o++] = b01
      data[o++] = a
    }
    strandRanges.push([start, drawCount])
  }

  // ── Particles: camera-facing quads with a tail→head alpha ramp ──
  let particleStart = o / 6
  if (pc > 0 && reveal >= 1) {
    for (let li = 0; li < s.count; li++) {
      if (s.particleRandom && hash(li, 7, s.particleSeed) > s.particleSparse)
        continue
      const pts = state.pointCache[li]
      if (!pts) continue
      for (let pi = 0; pi < pc; pi++) {
        const pos = particlePos(li, pi, time, s)
        if (pos == null) continue
        const [px, py, dx, dy] = pointAtArc(state, pts, pos.u)
        let variance = 1
        if (s.particleVariance > 0) {
          const e = hash(li, 31 * pi + 5, s.particleSeed)
          variance = Math.max(
            0.1,
            1 - s.particleVariance + 2 * e * s.particleVariance
          )
        }
        const [cr, cg, cb] = glColor(particleColor(li, pi, s))
        const mag = Math.hypot(dx, dy) || 1
        const bx = dx / mag
        const by = dy / mag
        const half = s.particleLength / 2
        const hw = (s.particleSize * variance) / 2
        const nx = -by * hw
        const ny = bx * hw
        const tx = px - bx * half
        const ty = py - by * half
        const hx = px + bx * half
        const hy = py + by * half
        const aTail = s.particleFade ? 0 : pos.fade
        const aHead = pos.fade
        // two triangles: (t+n, t-n, h+n) and (t-n, h-n, h+n)
        const quad: [number, number, number][] = [
          [tx + nx, ty + ny, aTail],
          [tx - nx, ty - ny, aTail],
          [hx + nx, hy + ny, aHead],
          [tx - nx, ty - ny, aTail],
          [hx - nx, hy - ny, aHead],
          [hx + nx, hy + ny, aHead],
        ]
        for (const [qx, qy, qa] of quad) {
          data[o++] = qx
          data[o++] = qy
          data[o++] = cr
          data[o++] = cg
          data[o++] = cb
          data[o++] = qa
        }
      }
    }
  }
  const particleCountVerts = o / 6 - particleStart

  gl.bufferData(gl.ARRAY_BUFFER, data.subarray(0, o), gl.DYNAMIC_DRAW)
  for (const [start, count] of strandRanges)
    gl.drawArrays(gl.LINE_STRIP, start, count)
  if (particleCountVerts > 0)
    gl.drawArrays(gl.TRIANGLES, particleStart, particleCountVerts)
}

export function RibbonCanvas({
  settings,
  className,
  maxDpr = 2,
  pixelBudget = 0,
  maxFps = 0,
}: {
  settings?: Partial<RibbonSettings>
  className?: string
  maxDpr?: number
  /**
   * Cap on the canvas backing store in device pixels (0 = off). Only useful
   * for the 2D fallback path — the WebGL renderer runs full-resolution.
   */
  pixelBudget?: number
  /** Frame-rate cap for the animation. 0 disables (native rAF rate). */
  maxFps?: number
}) {
  const merged = useMemo(
    () => ({ ...DEFAULT_SETTINGS, ...settings }),
    [settings]
  )
  const settingsRef = useRef(merged)
  const ref = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    settingsRef.current = merged
  }, [merged])

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return

    // Prefer the GPU renderer; fall back to 2D with conservative caps.
    let glr = initGl(canvas)
    const ctx = glr ? null : canvas.getContext('2d')
    if (!glr && !ctx) return

    // GPU context loss (e.g. the browser evicting contexts) must not leave
    // a permanently blank canvas: preventDefault opts into restoration, and
    // on restore all GL resources are rebuilt on the same context.
    const onContextLost = (e: Event) => e.preventDefault()
    const onContextRestored = () => {
      const next = initGl(canvas)
      if (next) glr = next
    }
    if (glr) {
      canvas.addEventListener('webglcontextlost', onContextLost)
      canvas.addEventListener('webglcontextrestored', onContextRestored)
    }

    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    const state = createRibbonState()

    // The 2D fallback needs protecting from itself on big canvases.
    const budget = glr ? pixelBudget : pixelBudget || 1_400_000
    const fpsCap = glr ? maxFps : maxFps || 30

    const render = (time: number, reveal: number) => {
      const s = settingsRef.current
      if (glr) {
        drawRibbonFrameGl(glr, state, s, time, reveal)
        return
      }
      // cover-scale the virtual field into the canvas, centered
      const scale = Math.max(canvas.width / s.width, canvas.height / s.height)
      const ox = (canvas.width - s.width * scale) / 2
      const oy = (canvas.height - s.height * scale) / 2
      ctx!.setTransform(scale, 0, 0, scale, ox, oy)
      drawRibbonFrame(state, ctx!, s, time, reveal)
    }

    // One synchronous draw right after a buffer reallocation, so a cleared
    // backing store is never painted. Reassigned below once the animation
    // clock exists; until then a static full-reveal frame is fine.
    let renderNow = () => render(0, 1)

    const applySize = () => {
      let dpr = Math.min(globalThis.devicePixelRatio || 1, maxDpr)
      const rect = canvas.getBoundingClientRect()
      if (budget > 0) {
        const px = rect.width * rect.height * dpr * dpr
        if (px > budget) dpr *= Math.sqrt(budget / px)
      }
      const w = Math.max(1, Math.round(rect.width * dpr))
      const h = Math.max(1, Math.round(rect.height * dpr))
      if (w === canvas.width && h === canvas.height) return
      // Assigning width/height clears the buffer — redraw before paint.
      canvas.width = w
      canvas.height = h
      renderNow()
    }
    applySize()

    // While a resize drag is in flight the canvas simply CSS-stretches its
    // last baked frame (no clearing, no blank flash); the backing store is
    // re-baked once the size settles. Per-tick reallocation was the source
    // of the resize flicker.
    let resizeTimer = 0
    const ro = new ResizeObserver(() => {
      clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(applySize, 150)
    })
    ro.observe(canvas)
    if (reduced)
      return () => {
        clearTimeout(resizeTimer)
        ro.disconnect()
      }

    let raf = 0
    let running = false
    let pausedAt = 0
    let startTs = 0
    let revealStart = 0
    let revealed = false
    let lastDraw = 0
    const minFrameMs = fpsCap > 0 ? 1000 / fpsCap - 1 : 0

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame)
      if (minFrameMs > 0 && now - lastDraw < minFrameMs) return
      lastDraw = now
      if (startTs === 0) startTs = now
      if (revealStart === 0) revealStart = now
      const t = ((now - startTs) / 1000) * settingsRef.current.speed
      let reveal = 1
      if (!revealed) {
        reveal = Math.min((now - revealStart) / 1500, 1)
        if (reveal >= 1) revealed = true
      }
      render(t, reveal)
    }

    // Re-bake with the animation's current time so the settled-size redraw
    // is indistinguishable from a normal frame (paused or running).
    renderNow = () => {
      const now = performance.now()
      const t = startTs
        ? ((now - startTs) / 1000) * settingsRef.current.speed
        : 0
      const reveal = revealed
        ? 1
        : revealStart
          ? Math.min((now - revealStart) / 1500, 1)
          : 0
      render(t, reveal)
    }

    const stop = () => {
      if (running) {
        running = false
        cancelAnimationFrame(raf)
        pausedAt = performance.now()
      }
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          if (!running) {
            running = true
            if (pausedAt) {
              const delta = performance.now() - pausedAt
              startTs += delta
              if (revealStart) revealStart += delta
            }
            raf = requestAnimationFrame(frame)
          }
        } else stop()
      },
      { rootMargin: '10% 0px' }
    )
    io.observe(canvas)

    return () => {
      stop()
      clearTimeout(resizeTimer)
      io.disconnect()
      ro.disconnect()
      canvas.removeEventListener('webglcontextlost', onContextLost)
      canvas.removeEventListener('webglcontextrestored', onContextRestored)
    }
  }, [maxDpr, pixelBudget, maxFps])

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className={cn('block size-full', className)}
    />
  )
}
